// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { useAppNavigate } from '@client-app/hooks';
import { type FC, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouteError, useNavigate } from 'react-router';

export const NotFound: FC<{
  error?: boolean;
  navigation?: boolean;
  message?: string;
}> = ({ error, navigation = true, message }) => {
  // Inicializamos con los namespaces necesarios
  const { t } = useTranslation(['errors', 'common']);

  const navigate = useAppNavigate();
  const routerNavigate = useNavigate();

  const errorState = useRouteError();

  const onBackClick = useCallback(() => {
    if (error && errorState) {
      routerNavigate('', { replace: true });
    }

    navigate('/');
  }, [navigate, routerNavigate, error, errorState]);

  const errorMessage =
    message ??
    (error
      ? t('errors.unexpected-error', { ns: 'errors' })
      : t('errors.404-description', { ns: 'errors' }));

  useEffect(() => console.error(errorState), [errorState]);

  return (
    <section className="flex min-h-screen flex-col place-content-center place-items-center font-poppins">
      <h1 className="mb-5 text-[150px] font-bold text-primary">
        {error
          ? t('errors.error-title', { ns: 'errors' })
          : t('errors.404-error-title', { ns: 'errors' })}
      </h1>

      <p className="mb-20 text-3xl font-light leading-6 text-primary-medium">
        {errorMessage}
      </p>

      {navigation && (
        <button
          className="inline-flex h-[45px] place-content-center place-items-center rounded border-2 border-primary !py-2 px-8 font-poppins text-xl font-semibold leading-normal text-primary"
          onClick={onBackClick}
          type="button"
        >
          {t('common.go-back', { ns: 'common' })}
        </button>
      )}
    </section>
  );
};

export default NotFound;
